import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  CssBaseline,
  Drawer,
  Toolbar,
  Divider,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// custom components
import SideNav from "./header-and-sidenavbar/sidenav";
import Headers from "./header-and-sidenavbar/header";

// pages
import AddOnBoarding from "./pages/bussiness-partner/add-onboarding";
import OnBoarding from "./pages/bussiness-partner/onboarding";
import EditOnBoarding from "./pages/bussiness-partner/edit-onboarding";
import AcountOnBoarding from "./pages/bussiness-partner/acounts-onboarding";
import AccountHead from "./pages/bussiness-partner/super-admin-account-heaad-approve";
import AccountTeam from "./pages/bussiness-partner/super-admin-account-team";
import Management from "./pages/bussiness-partner/super-admin-management";
import PartnerDetails from "./pages/bussiness-partner/partner-details";
import GeneralSetting from "./pages/general-setting/genaral-setting";
import WTAXMaster from "./pages/sap-master/wtax-master";
import BankMaster from "./pages/sap-master/bank-master";
import VendorPay from "./pages/sap-master/vendorpay";
import GroupMaster from "./pages/sap-master/group-master";
import DownPayAccount from "./pages/sap-master/down-pay-acount";
import CustomerReceivables from "./pages/sap-master/customer-recivible";
import BranchMaster from "./pages/prefered-master/branch-master";
import AgreementMaster from "./pages/prefered-master/aggrement-master";
import BusinessChannel from "./pages/prefered-master/business-channel";
import StateMaster from "./pages/prefered-master/state-master";
import ItemDetailMaster from "./pages/itemDetailMaster/ItemDetailMaster";
import DistributorMaster from "./pages/distributor/DistributorMaster";
import RetailerMaster from "./pages/retailer-master/RetailerMaster";
import OrderDetailMaster from "./pages/orderDetailMaster/OrderDetailMaster";
import OrderReport from "./pages/orderReport/OrderReport";
import PurchaseOrder from "./pages/purchase-order/PurchaseOrder";
import AddUser from "./pages/add-users.tsx/add-user";
import { useCommonContext } from "./Utils/commonContext";

export const drawerWidth = 260;
export const miniDrawerWidth = 65;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  isMobile?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})<AppBarProps>(({ theme, open, isMobile }) => ({
  boxShadow: "none",
  backgroundColor: isMobile ? "var(--primary-color)" : "var(--bg-color)",
  color: "#000",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(!isMobile && {
    width: open
      ? `calc(100% - ${drawerWidth}px)`
      : `calc(100% - ${miniDrawerWidth}px)`,
    marginLeft: open ? drawerWidth : miniDrawerWidth,
  }),
  ...(open &&
    !isMobile && {
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: "60px",
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})<{ open?: boolean; isMobile?: boolean }>(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  backgroundColor: "#F0F1F7",
  minHeight: "100dvh",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(isMobile
    ? {
        width: "100%",
        marginLeft: 0,
      }
    : {
        width: `calc(100% - ${open ? drawerWidth : miniDrawerWidth}px)`,
        marginLeft: open ? drawerWidth : 0,
      }),

  ...(open &&
    !isMobile && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
}));

const RoutePage: React.FC = () => {
  const theme = useTheme();
  const { open, setOpen, isMobile } = useCommonContext();
  const location = useLocation();

  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [location, isMobile]);

  return (
    <Box sx={{ display: "flex", width: "100dvw" }}>
      <CssBaseline />

      {/* Header */}
      <AppBar position="fixed" open={open} isMobile={isMobile}>
        <Toolbar className="mui_toolbar_container">
          <Headers isOpen={open} setIsOpen={setOpen} />
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        sx={{
          width: isMobile
            ? open
              ? drawerWidth
              : 0
            : open
            ? drawerWidth
            : miniDrawerWidth,

          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isMobile
              ? open
                ? drawerWidth
                : 0
              : open
              ? drawerWidth
              : miniDrawerWidth,

            boxSizing: "border-box",
            border: "none",
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={isMobile ? open : true}
        onClose={() => isMobile && setOpen(false)}
      >
        <SideNav isOpen={open} setIsOpen={setOpen} />
      </Drawer>

      {/* Main Content */}
      <Main open={open} isMobile={isMobile}>
        <DrawerHeader />
        <Routes>
          {/* Business Partner */}
          <Route
            path="/business-partner/onboarding/add"
            element={<AddOnBoarding />}
          />
          <Route path="/business-partner/onboarding" element={<OnBoarding />} />
          <Route
            path="/business-partner/onboarding/edit"
            element={<EditOnBoarding />}
          />
          <Route
            path="/business-partner/acounts-onboarding"
            element={<AcountOnBoarding />}
          />
          <Route
            path="/business-partner/account-head-approve"
            element={<AccountHead />}
          />
          <Route
            path="/business-partner/account-team-approve"
            element={<AccountTeam />}
          />
          <Route
            path="/business-partner/management-approve"
            element={<Management />}
          />
          <Route
            path="/business-partner/partner-details"
            element={<PartnerDetails />}
          />

          {/* SAP Master */}
          <Route path="/sap-master/wtax-master" element={<WTAXMaster />} />
          <Route path="/sap-master/bank-master" element={<BankMaster />} />
          <Route path="/sap-master/vendor-pay" element={<VendorPay />} />
          <Route path="/sap-master/group-master" element={<GroupMaster />} />
          <Route
            path="/sap-master/downpayaccount"
            element={<DownPayAccount />}
          />
          <Route
            path="/sap-master/customer-receivables"
            element={<CustomerReceivables />}
          />

          {/* Preferred Master */}
          <Route
            path="/prefer-master/branch-master"
            element={<BranchMaster />}
          />
          <Route
            path="/prefer-master/agreement-master"
            element={<AgreementMaster />}
          />
          <Route
            path="/prefer-master/business-channel"
            element={<BusinessChannel />}
          />
          <Route path="/prefer-master/state-master" element={<StateMaster />} />
          <Route
            path="/prefer-master/itemdetail-master"
            element={<ItemDetailMaster />}
          />
          <Route
            path="/prefer-master/distributor"
            element={<DistributorMaster />}
          />

          {/* Others */}
          <Route path="/general-setting" element={<GeneralSetting />} />
          <Route path="/add-user" element={<AddUser />} />

          {/* Order Management */}
          <Route
            path="order-management/retailer"
            element={<RetailerMaster />}
          />
          <Route
            path="order-management/order-master"
            element={<OrderDetailMaster />}
          />
          <Route
            path="order-management/order-report"
            element={<OrderReport />}
          />

          {/* Purchase Order */}
          <Route path="purchase-order" element={<PurchaseOrder />} />
        </Routes>
      </Main>
    </Box>
  );
};

export default RoutePage;
